import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { shopInfoKey } from '../../shared/constants/storage-key-constants';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { UserRole } from './../../shared/enum/user-role.enum';
import { Shop } from './../../shared/models/shop.model';
import { SplunkConfig } from './../../shared/models/splunk-config.model';
import { SplunkPayload } from './../../shared/models/splunk-payload.model';
import { SplunkWorkerMessage } from './../../shared/models/splunk-worker-message.model';
import { AuthService } from './../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  worker: Worker = null;
  workerObject: SplunkWorkerMessage = <SplunkWorkerMessage>{};
  splunkConfig: SplunkConfig = new SplunkConfig();

  constructor(
    private sessionStorageService: SessionStorageService,
    private authService: AuthService,
    private injector: Injector,
    private zone: NgZone,
    private http: HttpClient
  ) {
    this.initializeWebWorker();
  }

  /**
   * Function to initialize worker to load log JS file
   *
   * @memberof LoggerService
   */
  initializeWebWorker() {
    if (typeof Worker !== 'undefined') {
      if (!this.worker) {
        this.worker = new Worker('./splunk-log.worker', { type: 'module' });
      }
    }
  }

  /**
   * To set the logger level to DEBUG
   */
  logDebug(message: any): void {
    this.postToWorker('debug', message);
  }

  /**
   * To set the logger level to ERROR
   */
  logError(message: any): void {
    this.postToWorker('error', message);
  }

  /**
   * To set the logger level to WARNING
   */
  logWarning(message: any): void {
    this.postToWorker('warning', message);
  }

  /**
   * To set the logger level to INFO
   */
  logInfo(message: any): void {
    this.postToWorker('info', message);
  }

  postToWorker(mode: string, message: string) {
    this.workerObject.mode = mode;
    this.workerObject.splunkConfig = this.splunkConfig;
    this.workerObject.payLoad = this.getSplunkPayLoad(this.getLogObject(message));
    if (this.worker) {
      this.worker.postMessage(this.workerObject);
    } else {
      this.zone.runOutsideAngular(() => {
        this.triggerApiRequest();
      });
    }
  }

  getLogObject(msg: any) {
    const shopInfo: Shop = this.getShopInfo();
    const userId = (shopInfo && shopInfo.userId) ? shopInfo.userId : '';
    const shoplistUid = (shopInfo && shopInfo.shoplistUid) ? shopInfo.shoplistUid : '';
    const isCustomerSession: boolean = this.authService.isRoleAvailable(UserRole.CUSTOMER);
    const userInSession = (isCustomerSession) ? UserRole.CUSTOMER : UserRole.SHOP_OWNER;
    const sessionId: string = this.sessionStorageService.getSessionInfo();
    let message = '';
    try {
      const location = this.injector.get(LocationStrategy);
      const url = location instanceof PathLocationStrategy ? location.path() : '';
      const dt = new Date();
      const loggingTime = dt.getHours() + ':' + dt.getMinutes() + ':' + dt.getSeconds() + ':' + dt.getMilliseconds();
      const userAgent = window.navigator.userAgent;
      const errorToSend = {
        sessionId, userAgent, user: userInSession,
        shop: { userId, shoplistUid }, url, message: msg, loggingTime
      };
      message = JSON.stringify(errorToSend);
    } catch (er) {
      message = '';
    }
    return message;
  }

  getShopInfo(): Shop {
    return <Shop>this.sessionStorageService.getItem(shopInfoKey);
  }

  private triggerApiRequest() {
    this.workerObject.payLoad.event.severity = this.workerObject.mode;
    const apiHeaders = new HttpHeaders().append('authorization', 'Splunk ' + this.workerObject.splunkConfig.token);
    this.http.post(this.workerObject.splunkConfig.url, this.workerObject.payLoad, { headers: apiHeaders })
      .subscribe();
  }

  private getSplunkPayLoad(logMessage: string): SplunkPayload {
    let payLoad: SplunkPayload;
    try {
      const shopInfo: Shop = this.getShopInfo();
      const shopUid = (shopInfo && shopInfo.shopUid) ? shopInfo.shopUid : '';
      payLoad = new SplunkPayload({ shopUid, message: logMessage });
    } catch (err) {
      // do nothing
    }

    return payLoad;
  }

}
